/* eslint-disable */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@ui';
import PortSummaryCard from '../../components/PortSummaryCard/PortSummaryCard';
import OpenMapsConfirmModal from '../../components/OpenMapsConfirmModal';
import { stripSocTierLinesFromNotes } from '../../utils/socDynamicPriceNotes';
import { formatDamNotesForDriver } from '../../utils/utils';
import { buildGoogleMapsNavigationUrl } from '../../pages/homePage/mapPortSearchUtils';

function resolveStationListNotes(station) {
  const noteLang = (localStorage.getItem('i18nextLng') || 'ua').split('-')[0];
  const preferUa = noteLang === 'ua' || noteLang === 'uk';
  const notesRaw = (preferUa ? station?.notesUa : station?.notesEn) || station?.notesUa || station?.notesEn || '';
  const withoutSoc = stripSocTierLinesFromNotes(notesRaw);
  return formatDamNotesForDriver(withoutSoc, preferUa).replace(/\s+/g, ' ').trim();
}

function StationListCardNotes({ station }) {
  const notesText = useMemo(() => resolveStationListNotes(station), [station?.notesUa, station?.notesEn]);

  if (!notesText) {
    return null;
  }

  return (
    <div className="station-list-card__notes">
      <p className="station-list-card__notes-text">{notesText}</p>
    </div>
  );
}

function buildListPortStatus(station, closestPortsDeviceStatus) {
  const job = station?.job;
  const number = station?.number;
  const isInProgress = job?.state === 'IN_PROGRESS';
  const powerWt = Number(job?.powerWt);
  const powerKw = isInProgress ? (Number.isFinite(powerWt) && powerWt > 0 ? powerWt / 1000 : 0) : null;
  const leftRaw = Number(job?.leftS);
  const leftSeconds = isInProgress && Number.isFinite(leftRaw) && leftRaw >= 0 ? Math.floor(leftRaw) : null;
  const numKey = number != null ? String(number) : '';
  const device =
    numKey && closestPortsDeviceStatus && Object.prototype.hasOwnProperty.call(closestPortsDeviceStatus, numKey)
      ? closestPortsDeviceStatus[numKey]
      : null;
  const isReady = device && typeof device === 'object' ? Boolean(device.isReady) : Boolean(device);
  const isOccupied = device && typeof device === 'object' ? Boolean(device.isOccupied) : false;

  return {
    isReady,
    isOccupied,
    isInProgress,
    isCreated: job?.state === 'CREATED' || false,
    powerKw,
    leftSeconds,
  };
}

export const StationCard = ({ station, closestPortsDeviceStatus, portCount = 1 }) => {
  const { job, number, latitude, longitude, distanceMeters, plugshareLink } = station;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mapsModalOpen, setMapsModalOpen] = useState(false);

  const canOpenMaps = useMemo(() => Boolean(buildGoogleMapsNavigationUrl(latitude, longitude)), [latitude, longitude]);

  const portStatusOverride = useMemo(
    () => buildListPortStatus(station, closestPortsDeviceStatus),
    [station, closestPortsDeviceStatus]
  );

  const handleOpenPort = useCallback(() => {
    navigate(
      job?.state === 'IN_PROGRESS' || job?.state === 'WAITING'
        ? `/charging?station=${number}`
        : `/start?station=${number}`
    );
  }, [job?.state, navigate, number]);

  const distanceLabel =
    Number.isFinite(Number(distanceMeters)) && Number(distanceMeters) >= 0
      ? `${(Number(distanceMeters) / 1000).toFixed(1)}  ${t('distanceKm')}`
      : null;

  return (
    <div className="station-list-card">
      <PortSummaryCard
        station={station}
        portNumber={number}
        portCount={portCount}
        livePoll={false}
        portStatusOverride={portStatusOverride}
        className="station-list-card__port"
        ariaLabel={`${t('station')} ${number}`}
        onClick={handleOpenPort}
      />
      {(canOpenMaps || plugshareLink) && (
        <div className="station-list-card__actions">
          {canOpenMaps && distanceLabel && (
            <Button className="station-list-action-btn" variant="outline-alt" onClick={() => setMapsModalOpen(true)}>
              <Icon i="map" size="18px" />
              <span className="station-list-action-btn__label">{distanceLabel}</span>
            </Button>
          )}
          {plugshareLink && (
            <Button className="station-list-action-btn" link={plugshareLink} variant="outline-alt">
              <Icon i="feedback" size="18px" />
              <span className="station-list-action-btn__label">Plugshare</span>
            </Button>
          )}
        </div>
      )}
      <StationListCardNotes station={station} />
      {canOpenMaps ? (
        <OpenMapsConfirmModal
          modalOpen={mapsModalOpen}
          setModalOpen={setMapsModalOpen}
          latitude={latitude}
          longitude={longitude}
          stationNumber={number}
        />
      ) : null}
    </div>
  );
};
